import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import Swal from 'sweetalert2';

const GuideContainer = styled.div`
  background-color: var(--bg-sidebar);
  border-radius: 8px;
  padding: 20px;
  margin-top: 30px;
  box-shadow: var(--monday-card-shadow);
  
  .guide-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    h3 {
      margin: 0;
      color: var(--text-primary);
    }
    
    .ai-icon {
      width: 24px;
      height: 24px;
      margin-right: 10px;
      color: var(--primary);
    }
  }
  
  .guide-content {
    margin-bottom: 20px;
  }
  
  .guide-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
    
    textarea {
      min-height: 100px;
      padding: 12px;
      border-radius: 4px;
      border: 1px solid var(--border-color);
      background: var(--bg-card);
      color: var(--text-primary);
      font-family: inherit;
      resize: vertical;
      
      &:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2);
      }
    }
    
    .file-upload {
      border: 2px dashed var(--border-color);
      padding: 20px;
      border-radius: 4px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        border-color: var(--primary);
        background-color: rgba(var(--primary-rgb), 0.05);
      }
      
      input {
        display: none;
      }
      
      .file-list {
        margin-top: 10px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        
        .file-item {
          background-color: var(--bg-card);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 14px;
          display: flex;
          align-items: center;
          
          .remove-file {
            margin-left: 6px;
            cursor: pointer;
            color: var(--text-secondary);
            
            &:hover {
              color: var(--danger);
            }
          }
        }
      }
    }
  }
  
  .guide-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
    
    button {
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 500;
      
      &.btn-secondary {
        background-color: transparent;
        border: 1px solid var(--border-color);
        color: var(--text-primary);
        
        &:hover {
          background-color: var(--bg-hover);
        }
      }
      
      &.btn-primary {
        background-color: var(--primary);
        border: 1px solid var(--primary);
        color: white;
        
        &:hover {
          background-color: var(--primary-dark);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
  
  .response-container {
    margin-top: 20px;
    padding: 15px;
    background-color: var(--bg-card);
    border-radius: 4px;
    border-left: 3px solid var(--primary);
    
    pre {
      white-space: pre-wrap;
      word-break: break-word;
      margin: 0;
      font-family: 'Courier New', monospace;
      font-size: 14px;
    }
  }
  
  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 20px 0;
    
    .spinner {
      border: 3px solid rgba(var(--primary-rgb), 0.2);
      border-top: 3px solid var(--primary);
      border-radius: 50%;
      width: 24px;
      height: 24px;
      animation: spin 1s linear infinite;
      margin-right: 10px;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
`;

const AIGuide = ({ onFluxGenerated, onComplete }) => {
  console.log("AIGuide component rendering");
  
  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  
  // Check if Claude API is configured
  const [apiConfigured, setApiConfigured] = useState(false);
  
  useEffect(() => {
    // Check if the Claude API key is set in the environment
    const apiKey = process.env.REACT_APP_CLAUDE_API_KEY;
    console.log("API Key configured:", !!apiKey);
    setApiConfigured(!!apiKey && apiKey.length > 0);
  }, []);
  
  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };
  
  const handleFileUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };
  
  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };
  
  const handleSubmit = async () => {
    if (!prompt.trim() && files.length === 0) {
      setError('Please provide a prompt or upload files');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      
      files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
      
      // Send to Claude API endpoint
      const response = await axios.post(
        process.env.REACT_APP_API_URL + '/api/claude/v1/messages',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      if (response.data.flux) {
        // Pass the generated flux to the parent component
        if (onFluxGenerated) {
          onFluxGenerated(response.data.flux);
        } else if (onComplete) {
          onComplete(response.data.flux);
        }
      }
      
      setResponse(response.data);
    } catch (err) {
      console.error('Error processing request:', err);
      setError(err.response?.data?.message || 'An error occurred while processing your request');
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetForm = () => {
    setPrompt('');
    setFiles([]);
    setResponse(null);
    setError(null);
  };
  
  if (!apiConfigured) {
    return (
      <GuideContainer>
        <div className="guide-header">
          <div className="ai-icon">🤖</div>
          <h3>AI Guide</h3>
        </div>
        <p>The AI assistant is not configured. Please set the REACT_APP_CLAUDE_API_KEY in your environment variables.</p>
      </GuideContainer>
    );
  }
  
  return (
    <GuideContainer>
      <div className="guide-header">
        <div className="ai-icon">🤖</div>
        <h3>AI Guide</h3>
      </div>
      
      <div className="guide-content">
        <p>
          Describe what you're trying to accomplish with your bot, or upload relevant files 
          for our AI to analyze and suggest appropriate flux templates.
        </p>
      </div>
      
      <div className="guide-form">
        <textarea
          placeholder="Example: I want to build a bot that sends notifications when new Jira tickets are created and posts them to Slack..."
          value={prompt}
          onChange={handlePromptChange}
        />
        
        <div className="file-upload" onClick={() => document.getElementById('file-input').click()}>
          <input
            id="file-input"
            type="file"
            multiple
            onChange={handleFileUpload}
          />
          <p>Drop files here or click to browse</p>
          {files.length > 0 && (
            <div className="file-list">
              {files.map((file, index) => (
                <div key={index} className="file-item">
                  {file.name}
                  <span className="remove-file" onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}>×</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {error && (
          <div className="error-message" style={{ color: 'var(--danger)' }}>
            {error}
          </div>
        )}
      </div>
      
      {isLoading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Analyzing your requirements...</p>
        </div>
      )}
      
      {response && (
        <div className="response-container">
          <h4>AI Suggestions</h4>
          <pre>{response.message}</pre>
        </div>
      )}
      
      <div className="guide-footer">
        <button className="btn-secondary" onClick={resetForm}>
          Reset
        </button>
        <button
          className="btn-primary"
          onClick={handleSubmit}
          disabled={isLoading || (!prompt.trim() && files.length === 0)}
        >
          {isLoading ? 'Processing...' : 'Generate Suggestions'}
        </button>
      </div>
    </GuideContainer>
  );
};

export default AIGuide; 